import { Tabs as TabsPrimitive } from 'radix-ui';
import * as React from 'react';

import { VariantProps, cn, tv } from './utils/cn';

/**
 * Switch between several panels of related content. Compose with
 * {@link TabsList}, {@link TabsTrigger}, and {@link TabsContent}.
 *
 * @example
 * <Tabs defaultValue="overview">
 *   <TabsList>
 *     <TabsTrigger value="overview">Overview</TabsTrigger>
 *     <TabsTrigger value="history">History</TabsTrigger>
 *   </TabsList>
 *   <TabsContent value="overview">...</TabsContent>
 *   <TabsContent value="history">...</TabsContent>
 * </Tabs>
 */
function Tabs({
  className,
  orientation = 'horizontal',
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      orientation={orientation}
      className={cn(
        'group/tabs flex gap-2 data-horizontal:flex-col',
        className,
      )}
      {...props}
    />
  );
}

const tabsListVariants = tv({
  base: 'group/tabs-list inline-flex w-fit items-center justify-center rounded-lg border border-border p-px text-muted-foreground group-data-vertical/tabs:h-fit group-data-vertical/tabs:flex-col group-data-horizontal/tabs:h-10 data-[variant=line]:rounded-none data-[variant=line]:border-none',
  variants: {
    variant: {
      default: 'bg-muted',
      line: 'gap-1 bg-transparent',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

/**
 * Container for the {@link TabsTrigger} buttons. Pick `variant="default"` for
 * a pill-style tab bar on a muted track, or `variant="line"` for a flatter
 * look with an underline indicator.
 */
function TabsList({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      className={tabsListVariants({ variant, className })}
      {...props}
    />
  );
}

/**
 * Class list for a trigger inside {@link TabsList}. Exported so link-based
 * tab bars (e.g. router `NavLink`s) can render with identical styling;
 * consumers must reproduce the attribute contract these classes key on:
 * `data-state="active"` on the trigger (the `data-active:` variant matches
 * `[data-state='active']`) and a `data-variant` `group/tabs-list` container.
 */
const tabsTriggerStyles = cn(
  "relative inline-flex h-full flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-3 py-0.5 text-sm font-medium whitespace-nowrap text-foreground/60 transition-all group-data-vertical/tabs:w-full group-data-vertical/tabs:justify-start hover:text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5 dark:text-muted-foreground dark:hover:text-foreground group-data-[variant=default]/tabs-list:data-active:shadow-sm group-data-[variant=line]/tabs-list:data-active:shadow-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  'group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:data-active:bg-transparent dark:group-data-[variant=line]/tabs-list:data-active:border-transparent dark:group-data-[variant=line]/tabs-list:data-active:bg-transparent',
  'data-active:bg-background data-active:text-foreground dark:data-active:border-input dark:data-active:bg-input/30 dark:data-active:text-foreground',
  'after:absolute after:bg-bg-indigo-strong-primary after:opacity-0 after:transition-opacity group-data-vertical/tabs:after:inset-y-0 group-data-vertical/tabs:after:-right-0.5 group-data-vertical/tabs:after:w-0.5 group-data-horizontal/tabs:after:inset-x-0 group-data-horizontal/tabs:after:-bottom-0.5 group-data-horizontal/tabs:after:h-0.5 group-data-[variant=line]/tabs-list:data-active:after:opacity-100',
);

/**
 * Clickable tab. The `value` prop must match the corresponding
 * {@link TabsContent}'s `value`. Pass an `<Icon>` child with `data-icon` for
 * icon-decorated triggers.
 */
function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(tabsTriggerStyles, className)}
      {...props}
    />
  );
}

/**
 * Panel shown when its `value` matches the active tab. Pass `forceMount` to
 * keep the panel in the DOM when inactive (useful for preserving form state
 * or scroll position across tab switches).
 */
function TabsContent({
  className,
  forceMount,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn(
        'flex-1 text-sm outline-none',
        forceMount && 'data-inactive:hidden',
        className,
      )}
      forceMount={forceMount}
      {...props}
    />
  );
}

export {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  tabsListVariants,
  tabsTriggerStyles,
};
